import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

class EtPickerElement extends HTMLElement {
  connectedCallback() {
    const sectionRoot = this.closest('.shopify-section, dialog');
    sectionRoot?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantChange);
    this.#bindSelect();
    this.#initAutoET();
  }

  disconnectedCallback() {
    const sectionRoot = this.closest('.shopify-section, dialog');
    sectionRoot?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantChange);
    this.#cleanupAutoET();
  }

  #handleVariantChange = (event) => {
    const { data } = event.detail;
    const target = event.target;

    if (!data.newProduct && target instanceof HTMLElement &&
        target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    }

    const incoming = data.html.querySelector('et-picker');
    if (!incoming) return;

    // Remember the previously selected ET value before morph
    const oldSelect = this.querySelector('select');
    const oldEt = oldSelect ? parseInt((oldSelect.value || '').replace(/\D/g, ''), 10) : NaN;

    morph(this, incoming, { childrenOnly: true });
    this.#bindSelect();
    this.#clampAndDispatch(oldEt);
  };

  /** Attach change listener to the ET select dropdown and notify other components. */
  #bindSelect() {
    const select = this.querySelector('select');
    if (!select) return;
    select.addEventListener('change', () => {
      const val = parseInt((select.value || '').replace(/\D/g, ''), 10);
      if (!isNaN(val)) {
        window.dispatchEvent(new CustomEvent('et-picker:changed', { detail: { et: val } }));
      }
    });
  }

  /**
   * After morph, clamp the previously selected ET to the new dropdown range
   * and dispatch the updated value so other components stay in sync.
   */
  #clampAndDispatch(previousEt) {
    const select = this.querySelector('select');
    if (!select || select.options.length < 2) return;

    // Skip placeholder (options[0] is "Velg ET"), parse from options[1]
    const firstVal = parseInt((select.options[1]?.value || '').replace(/\D/g, ''), 10);
    const lastVal = parseInt((select.options[select.options.length - 1]?.value || '').replace(/\D/g, ''), 10);

    if (!isNaN(previousEt) && !isNaN(firstVal) && !isNaN(lastVal)) {
      const min = Math.min(firstVal, lastVal);
      const max = Math.max(firstVal, lastVal);
      const clamped = Math.max(min, Math.min(max, previousEt));
      select.value = `ET${clamped}`;
    }

    // Only dispatch if a real ET value is selected (not the placeholder)
    const val = parseInt((select.value || '').replace(/\D/g, ''), 10);
    if (!isNaN(val)) {
      window.dispatchEvent(new CustomEvent('et-picker:changed', { detail: { et: val } }));
    }
  }

  /* ── Auto-ET selection when Size/PCD changes ── */

  /**
   * Initialize auto-ET selection. Skips if vehicle-variant-filter is on
   * the page (it already handles this with vehicle-aware scoring).
   */
  #initAutoET() {
    if (document.querySelector('vehicle-variant-filter')) return;

    const jsonScript = document.querySelector('script[data-product-variants]');
    if (!jsonScript) return;

    try {
      this._variants = JSON.parse(jsonScript.textContent || '[]');
    } catch {
      return;
    }

    if (this._variants.length === 0) return;
    this.#attachOptionChangeListeners();
  }

  #cleanupAutoET() {
    if (this._optionChangeCleanup) {
      this._optionChangeCleanup();
      this._optionChangeCleanup = null;
    }
  }

  #getPicker() {
    const section = this.closest('.shopify-section') || document;
    return section.querySelector('variant-picker') || document.querySelector('variant-picker');
  }

  #getSelectedValue(picker, fieldsetIndex) {
    const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${fieldsetIndex}"]`);
    if (fieldset) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) return checked.value;
    }
    const selects = picker.querySelectorAll('select.variant-option__select');
    if (selects[fieldsetIndex]) return selects[fieldsetIndex].value;
    return null;
  }

  /**
   * Listen for changes on PCD (fieldset 0) and Size (fieldset 1) in
   * Horizon's variant picker. On change, auto-select best available ET.
   */
  #attachOptionChangeListeners() {
    this.#cleanupAutoET();

    const picker = this.#getPicker();
    if (!picker) return;

    const self = this;
    const cleanupFns = [];

    [0, 1].forEach((idx) => {
      const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${idx}"]`);
      if (!fieldset) return;

      const handler = () => {
        requestAnimationFrame(() => {
          self.#selectBestAvailableET(picker);
        });
      };

      fieldset.addEventListener('change', handler);
      cleanupFns.push(() => fieldset.removeEventListener('change', handler));
    });

    // Re-attach after Horizon morphs the variant picker DOM
    const sectionRoot = this.closest('.shopify-section, dialog');
    if (sectionRoot) {
      const morphHandler = () => {
        requestAnimationFrame(() => self.#attachOptionChangeListeners());
      };
      sectionRoot.addEventListener('variant:update', morphHandler);
      cleanupFns.push(() => sectionRoot.removeEventListener('variant:update', morphHandler));
    }

    this._optionChangeCleanup = () => cleanupFns.forEach((fn) => fn());
  }

  /**
   * Given current PCD + Size, find the best available ET variant and
   * select it in Horizon's variant picker (fieldset 2).
   */
  #selectBestAvailableET(picker) {
    const currentOpt1 = this.#getSelectedValue(picker, 0);
    const currentOpt2 = this.#getSelectedValue(picker, 1);
    if (!currentOpt1 || !currentOpt2) return;

    const matching = this._variants.filter(
      (v) => v.option1 === currentOpt1 && v.option2 === currentOpt2
    );
    if (matching.length === 0) return;

    // Score: prefer in-stock (+100), first match wins as tiebreaker
    let best = null;
    let bestScore = -Infinity;
    for (const v of matching) {
      const score = v.available ? 100 : 0;
      if (score > bestScore) {
        bestScore = score;
        best = v;
      }
    }

    if (!best) return;

    // Select ET in fieldset[2] — radio buttons
    const fieldset = picker.querySelector('fieldset[data-fieldset-index="2"]');
    if (fieldset) {
      const radio = fieldset.querySelector(
        `input[type="radio"][value="${CSS.escape(best.option3)}"]`
      );
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Select ET in fieldset[2] — select dropdown (mobile)
    const selects = picker.querySelectorAll('select.variant-option__select');
    const select = selects[2];
    if (select && select.value !== best.option3) {
      select.value = best.option3;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update URL
    if (best.id) {
      const url = new URL(window.location);
      url.searchParams.set('variant', String(best.id));
      window.history.replaceState({}, '', url);
    }
  }
}

if (!customElements.get('et-picker')) {
  customElements.define('et-picker', EtPickerElement);
}
